import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useSettingsStore } from '@/stores/settings-store';

/**
 * KAN-125: with "Multi-country contacts" on, the default country code is
 * disabled, so bare numbers are sent as typed and fail. Callers render this
 * whenever the recipient field has something in it, and hide it on an empty
 * form.
 *
 * "Turn off" clears the preference; note the default country code was wiped
 * when multi-country was switched on, so the caller should focus its
 * country-code input via `onTurnedOff` for the user to set one.
 */
export function MultiCountryWarning({ onTurnedOff, className }: { onTurnedOff?: () => void; className?: string }) {
  const updatePreferences = useSettingsStore((s) => s.updatePreferences);
  return (
    <div
      data-testid="multi-country-warning"
      role="alert"
      className={cn(
        'flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-2 text-[11px] text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200',
        className
      )}
    >
      <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-px" />
      <div className="flex-1 min-w-0">
        <span className="font-medium">Multi-country contacts is on, so no default country code is applied.</span>{' '}
        Enter the phone number with its country code (e.g. +91 98765 43210), or turn off Multi-country contacts
        and then set a default country code to have it applied automatically.
      </div>
      <Button
        type="button"
        variant="outline"
        size="sm"
        data-testid="multi-country-warning-disable"
        onClick={() => {
          updatePreferences({ multiCountryContacts: false });
          onTurnedOff?.();
        }}
        className="h-6 shrink-0 rounded border-amber-300 bg-white/60 px-2 text-[11px] text-amber-900 hover:bg-white hover:text-amber-900 dark:border-amber-700 dark:bg-amber-900/40 dark:text-amber-100 dark:hover:bg-amber-900/70"
      >
        Turn off
      </Button>
    </div>
  );
}
